import { Combobox } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { guardArray } from "@msuite/utils/array";
import type { TranslationKey } from "@msuite/utils/locales";
import { guardNull } from "@msuite/utils/misc";
import type { Nullable } from "@msuite/utils/types";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useAuth } from "@/hooks/use-auth";
import { useProjects } from "@/hooks/use-projects";
import { ProjectsComboboxItem } from "../projects-combobox-item";

/** Props Interface */
interface ProjectSearchProps {
	defaultProjectId?: Nullable<string>;
	onChange: (projectId: string | null) => void;
	label?: TranslationKey | null;
	"data-cy"?: string;
	isDisabled?: boolean;
	isNullable?: boolean;
	/**
	 * Which of the readable sites the picker offers. `ownProjects` keeps the ones the caller is
	 * staffed on, the same predicate the order lists narrow by — a convenience over the rules
	 * rather than a substitute for them, since what may be picked is still what
	 * `read Project` returned. Absent, every readable site is offered.
	 */
	scope?: "organization" | "ownProjects";
}

interface ProjectSearchForm {
	projectId: string | null;
}

export function ProjectSearch({
	defaultProjectId,
	onChange,
	label = "projects.project_search",
	isDisabled,
	isNullable,
	scope,
	...props
}: ProjectSearchProps) {
	/** State */
	const [lastUpdated, setLastUpdated] = useState<string | null>(guardNull(defaultProjectId));

	/** Hooks */
	const methods = useForm<ProjectSearchForm>({
		defaultValues: {
			projectId: guardNull(defaultProjectId),
		},
	});
	const current = useWatch({ control: methods.control, name: "projectId" });

	const { t } = useTranslation();
	const { user } = useAuth();

	/** Query */
	const { projects } = useProjects();

	/** Effects */
	useEffect(() => {
		if (lastUpdated === current) return;
		if (current) {
			onChange(current);
			setLastUpdated(current);
		} else {
			onChange(null);
			setLastUpdated(null);
		}
	}, [current, onChange]);

	/** Computed */
	// The membership rows travel on the card, so the narrowing costs no second request and asks
	// the same question `_members` was embedded to answer. Any role counts: a deputy is on the
	// site as much as its manager is.
	const options = useMemo(() => {
		if (scope !== "ownProjects" || !user?.id) return projects;
		return guardArray(projects).filter((project) =>
			guardArray(project._members).some((member) => member.userId === user.id),
		);
	}, [projects, scope, user?.id]);

	/** Render */
	return (
		<Combobox
			control={methods.control}
			name="projectId"
			options={options}
			keyExtractor={(item) => item.id}
			isDisabled={isDisabled}
			isNullable={isNullable}
			displayValue={(item) => item.name}
			label={label !== null ? t(label) : undefined}
			placeholder={t("placeholders.search")}
			renderOption={(item) => <ProjectsComboboxItem item={item} />}
			isRequired={false}
			data-cy="project-search"
			{...props}
		/>
	);
}
